// Copyright (c) Ping. All rights reserved.
//
// Recovery restore → post-bootstrap hand-off (V3 mnemonic flow).
//
// The restore host decrypts the V3 blob and rehydrates the SDK +
// auth-layer identities, but the live SDK client doesn't exist yet
// (bootstrap runs after). So the host stashes the backup's DeviceGroup
// MLS state snapshot here and a one-shot importer runs once the session
// is authenticated (App.tsx calls `importPendingRecoverySnapshots`).
//
// Module-level (not persisted): a restore always flows into bootstrap in
// the same load, and the secret-bearing snapshot must not linger in
// storage.
//
// NOTE: the old V2 flow stashed external-conversation snapshots here
// (re-exported from @ping/ui). V3 drops conversation snapshots entirely
// for forward secrecy and carries ONLY the DeviceGroup snapshot.

import { useSessionStore } from '@ping/ui/src/messaging/sessionStore';

import { log } from '@core/log/log';

interface SnapshotImportClient {
  importStateSnapshot(snapshotBytes: Uint8Array): Promise<unknown>;
}

function liveClient(): SnapshotImportClient | null {
  const session = useSessionStore.getState();
  if (session.status.kind !== 'authenticated') return null;
  return session.status.client as unknown as SnapshotImportClient;
}

let pendingDeviceGroupSnapshot: Uint8Array | null = null;

/** Called by the restore host with the decrypted backup's DeviceGroup snapshot. */
export function stashPendingDeviceGroupSnapshot(snapshot: Uint8Array): void {
  pendingDeviceGroupSnapshot = snapshot;
}

export function hasPendingRecoverySnapshots(): boolean {
  return pendingDeviceGroupSnapshot !== null;
}

/**
 * Import the stashed DeviceGroup snapshot into the live SDK client, then
 * drain the buffer. Idempotent + best-effort: no-ops unless a restore
 * stashed a snapshot this load, and a failed import is logged + dropped.
 * Safe to call on every authenticated transition.
 */
export async function importPendingRecoverySnapshots(): Promise<void> {
  const snapshot = pendingDeviceGroupSnapshot;
  if (snapshot === null) return;
  const client = liveClient();
  if (!client) return;
  pendingDeviceGroupSnapshot = null;
  try {
    await client.importStateSnapshot(snapshot);
    log.info({ event: 'recovery.v3.deviceGroupSnapshotImported' });
  } catch (err) {
    log.warn({
      event: 'recovery.v3.deviceGroupSnapshotImportFailed',
      message: (err as Error).message,
    });
  }
}
